import { useMemo } from 'react';
import { useReports, useReportsBulk } from '/@/hooks/reports';
import type { Field } from '/@/types/field';
import {
    FIELDVIEW_RECENT_PLANTING_DAYS,
    SUPPORTED_FV_CROPS,
} from '/@/views/fields/field-monitoring/components/creation-dialog/utils';

export type PlantingMergedResult = {
    cropName: string;
    plantingDate: string;
    plantedArea: number;
    seedProductNames: string[];
    report: number;
};

/** Extract the most recent relevant planting result for a field from a raw API response. */
const extractPlantingResult = (
    field: Field,
    reportData: { results: { field: number; mergedResults: PlantingMergedResult[] }[] } | undefined
): PlantingMergedResult | null => {
    if (!reportData) return null;

    const lastCropMonitoring = (field.cropMonitoring ?? [])
        .filter((cm): cm is typeof cm & { harvestDate: string } => !cm.active && !!cm.harvestDate)
        .sort((a, b) => new Date(b.harvestDate).getTime() - new Date(a.harvestDate).getTime())[0];

    const lastCMEndDate = lastCropMonitoring ? new Date(lastCropMonitoring.harvestDate) : null;
    const days180Ago = new Date(Date.now() - FIELDVIEW_RECENT_PLANTING_DAYS * 24 * 60 * 60 * 1000);

    const results: PlantingMergedResult[] = (reportData.results ?? [])
        .flatMap((r) => r.mergedResults ?? [])
        .filter((r) => SUPPORTED_FV_CROPS.includes(r.cropName));

    return (
        results
            .filter((result) => {
                const date = new Date(result.plantingDate);
                return date >= days180Ago || (lastCMEndDate && date > lastCMEndDate);
            })
            .sort((a, b) => new Date(b.plantingDate).getTime() - new Date(a.plantingDate).getTime())[0] ?? null
    );
};

/**
 * Fetch planting reports for a list of fields in a SINGLE request (bulk).
 * Returns a map from field id → PlantingMergedResult | null.
 * Use this at the list level to avoid N+1 HTTP requests.
 */
export const usePlantingReportResultBulk = (fields: Field[]): Map<number, PlantingMergedResult | null> => {
    const eligibleFieldIds = useMemo(() => fields.filter((f) => !f.activeCropMonitoring).map((f) => f.id), [fields]);

    const bulkQuery = useReportsBulk(
        { fieldIds: eligibleFieldIds, type: 'planting' },
        { enabled: eligibleFieldIds.length > 0 }
    );

    return useMemo(() => {
        const map = new Map<number, PlantingMergedResult | null>();
        if (!bulkQuery.data) return map;

        // Group results by field_id then extract for each field
        const byField = new Map<number, { results: { field: number; mergedResults: PlantingMergedResult[] }[] }>();
        for (const row of bulkQuery.data.results ?? []) {
            const fid: number = row.field;
            if (!byField.has(fid)) byField.set(fid, { results: [] });
            byField.get(fid)!.results.push(row);
        }

        for (const field of fields) {
            if (field.activeCropMonitoring) {
                map.set(field.id, null);
                continue;
            }
            const data = byField.get(field.id) ?? { results: [] };
            map.set(field.id, extractPlantingResult(field, data));
        }
        return map;
    }, [fields, bulkQuery.data]);
};

/**
 * Single-field version — kept for detail views (field monitoring detail) that
 * load one field at a time. For list views, prefer usePlantingReportResultBulk.
 */
export const usePlantingReportResult = (field: Field | undefined) => {
    const plantingReportsQuery = useReports(
        { fieldId: field?.id ?? 0, type: 'planting' },
        { enabled: !!field && !field.activeCropMonitoring }
    );

    return useMemo(() => {
        if (!field || field.activeCropMonitoring) return null;
        return extractPlantingResult(field, plantingReportsQuery.data);
    }, [field, plantingReportsQuery.data]);
};
